import { WithBase, WithRef } from "@converge-collective/common/models/Base";
import {
  Group,
  joinGroup,
  liteGroup,
} from "@converge-collective/common/models/Group";
import {
  GroupLog,
  GroupLogKinds,
} from "@converge-collective/common/models/GroupLog";
import { Network } from "@converge-collective/common/models/Network";
import {
  GroupCreatedPostRaw,
  POST_TYPE,
} from "@converge-collective/common/models/Post";
import { liteProfile } from "@converge-collective/common/models/Profile";
import { SubCategoryWithCategory } from "@converge-collective/common/models/SubCategory";
import GroupsIcon from "@mui/icons-material/Groups";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Box,
  Dialog,
  FormHelperText,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { first, isEmpty, kebabCase } from "lodash";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useFirestore, useUser } from "reactfire";
import Uploader, { baseUploadPath } from "~/components/Uploader";
import useProfile from "~/hooks/useProfile";
import { createAlert } from "~/lib/globalAlerts";
import { groupDetailRoute } from "~/routes";
import AutocompleteCategory from "../AutocompleteCategory";

type Props = {
  network: WithRef<Network>;
  isOpen: boolean;
  handleClose: () => void;
  handleGroupCreated: () => void;
};
export default function CreateGroup({
  isOpen,
  handleClose,
  handleGroupCreated,
  network,
}: Props): React.ReactElement {
  const router = useRouter();
  const firestore = useFirestore();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [interestedSubCategories, setInterestedSubCategories] =
    useState<SubCategoryWithCategory[]>();
  const [photoUrl, setPhotoUrl] = useState<string>();
  const [photosUploading, setPhotosUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState("");

  const { data: user } = useUser();
  const [profile] = useProfile(user?.uid);
  const loading = false;

  // if we keep user id in the path we can create more granular permissions
  const uploadPath = `${baseUploadPath}/groups/${user?.uid}`;

  const resetForm = () => {
    setName("");
    setDescription("");
    setPhotoUrl(undefined);
    setIsSubmitting(false);
    setValidationErrors([]);
    setErrorMessage("");
  };

  // reset the form every time the modal is opened
  useEffect(() => {
    resetForm();
  }, [isOpen]);

  const localHandlClose = () => {
    resetForm();
    handleClose();
  };

  const validate = (photoUrl: string | undefined) => {
    const errs = [
      ...(isEmpty(description) ? ["Description"] : []),
      ...(isEmpty(name) ? ["Name"] : []),
      ...(isEmpty(photoUrl) ? ["Cover Photo"] : []),
      // ...(isEmpty(subCategory) ? ["Category"] : []),
      // ...(!startAt ? ["Start at"] : []),
      // allow endAt to be optional
    ];
    setValidationErrors(errs);
    return errs;
  };

  const upload = async () => {
    if (photoUrl) return photoUrl;

    const photoUrls = uploadFn && (await uploadFn());
    const firstPhotoUrl = first(photoUrls);
    console.log("upload complete", { firstPhotoUrl });
    firstPhotoUrl && setPhotoUrl(firstPhotoUrl);
    return firstPhotoUrl;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage("");

    // override state by waiting for the upload. This is a bit hacky but it works
    const photoUrl = await upload();
    console.log("handleSubmit", { photoUrl });
    const errs = validate(photoUrl);

    if (photoUrl && profile && isEmpty(errs)) {
      const slug = kebabCase(name);
      try {
        // TODO all the firestore operations should be atomic so we don't for
        // instance create the group many times (if the user retries during an
        // error)
        const group: Group = {
          ...{
            name,
            slug,
            createdBy: liteProfile(profile),
            descriptionHtml: description,
            isStaffLed: false,
            isUserLed: true,
            isCategoryBased: false,
            photoUrl,
            leaders: [liteProfile(profile)],
            // These are the categories that the group is interested in. For a
            // category based group this is naturally the category of the group, but
            // for other types of groups it could be any subset of categories, e.g.
            // Tennis, Skiing, and Badminton. When Events are created for those
            // categories, they are automatically posted to the group, which
            // notifies everyone in the group.
            interestedSubCategoryIds: (interestedSubCategories || []).map(
              (scwc) => scwc.subCategory.id
            ),
            interestedSubCategories: (interestedSubCategories || []).map(
              (scwc) => scwc.subCategory
            ),
          },
          ...(!isEmpty(joinCode) ? { joinCode } : {}),
        };

        const groupRef = await network.ref.collection("groups").add(group);

        console.log("group created!", { group });
        const g: WithBase<WithRef<Group>> = {
          ...group,
          ref: groupRef,
          id: groupRef.id,
          createdAt: new Date(),
        };

        // create the log for event created
        const groupLog: GroupLog = {
          kind: GroupLogKinds.Created,
          date: new Date(),
          profile: liteProfile(profile),
          body: `${profile.name} created the ${name} group`,
          group: liteGroup(g),
        };
        groupRef.collection("groupLogs").add(groupLog);

        await joinGroup(network, g, profile);

        // create the newfeed post for the new group (only if it doesn't have a
        // joinCode)
        if (isEmpty(joinCode)) {
          const groupCreatedPost: GroupCreatedPostRaw = {
            eventType: POST_TYPE.GroupCreated,
            group: g,
            title: `${profile.name} created a new group: ${name}`,
            description,
            date: new Date(),
            avatar: profile.photoURL || "",
            profile: liteProfile(profile),
          };

          console.log("creating group post", { groupCreatedPost });

          // idempotent set
          await network.ref.collection("posts").doc(g.id).set(groupCreatedPost);
        }

        handleGroupCreated();
        localHandlClose();
        createAlert({
          message: `The ${group.name} Group was created!`,
          severity: "info",
        });
        // navigate to the new group!
        router.push(groupDetailRoute(network.slug, slug));
      } catch (e) {
        console.error(e);
        setErrorMessage(`Something went wrong: ${e.message}`);
        setIsSubmitting(false);
      }
    } else {
      profile ||
        console.warn(
          "profile was expected to be defined when creating a group",
          { profile }
        );
      console.info("validation errors", { errs });
    }

    setIsSubmitting(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSubmit();
  };

  const [uploadFn, setUploadFn] =
    useState<() => Promise<string[] | undefined>>();

  return (
    <>
      <Dialog
        maxWidth="xl"
        sx={{
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
        open={isOpen}
        onClose={localHandlClose}
        closeAfterTransition
      >
        <Paper sx={{ p: 2 }}>
          <Typography sx={{ mb: 4 }} color="primary" variant="h4">
            Create Group
          </Typography>

          {!loading && (
            <Grid container spacing={1}>
              <Grid sm={12} md={6} item>
                <TextField
                  onKeyPress={handleKeyPress}
                  value={name}
                  fullWidth
                  helperText="The name of the group!"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setName(event.target.value);
                  }}
                  error={isEmpty(name) && !isEmpty(validationErrors)}
                  required
                  label="Group Name"
                />
              </Grid>
              <Grid sm={12} md={6} item>
                <TextField
                  onKeyPress={handleKeyPress}
                  value={joinCode}
                  fullWidth
                  helperText="Optional: A Join Code lets you require users to enter the code when joining a group"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setJoinCode(event.target.value);
                  }}
                  label="Join code"
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <TextField
                  value={description}
                  fullWidth={true}
                  multiline={true}
                  helperText="What's this group all about?"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setDescription(event.target.value);
                  }}
                  error={isEmpty(description) && !isEmpty(validationErrors)}
                  required
                  label="Description"
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <AutocompleteCategory
                  multiple={true}
                  required={false}
                  helperText="Optional: Pick any categories this group is interested in or is related to"
                  onSetSubCategories={(subCats: SubCategoryWithCategory[]) => {
                    console.log("CreateGroup subcats", { subCats });
                    setInterestedSubCategories(subCats);
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ my: 2 }}>
                  <Uploader
                    uploadPath={uploadPath}
                    onResetPhotos={() => {
                      setPhotoUrl(undefined);
                    }}
                    onPhotosUploadStart={() => setPhotosUploading(true)}
                    onPhotosUploaded={({ photoUrls }) => {
                      console.log("photos uploaded!!", { photoUrls });
                      setPhotosUploading(false);
                      setPhotoUrl(photoUrls[0]);
                    }}
                    onReadyToUpload={(thunk) => {
                      console.log("on ready to upload!", { thunk });
                      // clear the photo when the user selects it, so that we
                      // know we still need to upload it!
                      setPhotoUrl(undefined);
                      setUploadFn(() => thunk);
                    }}
                    uploadButtonText="Add Group Cover Photo "
                    allowMultipleFiles={false}
                    allowCrop={true}
                    avatarEditorProps={{
                      width: 1200,
                      height: 325,
                    }}
                  />
                  <FormHelperText sx={{ textAlign: "center" }}>
                    Min width 1200px, min height 325px
                  </FormHelperText>
                </Box>
              </Grid>

              <Grid item xs={12} sm={12}>
                {!isEmpty(errorMessage) ? (
                  <Alert severity="error">{errorMessage}</Alert>
                ) : null}
                {validationErrors.length > 0 && (
                  <Alert severity="error">
                    <AlertTitle>
                      Please enter all required fields order to create a group:
                    </AlertTitle>

                    <ul>
                      {validationErrors.map((v) => (
                        <li key={v}>{v}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                <Box display="flex" mt={2} justifyContent="flex-end">
                  <LoadingButton
                    startIcon={<GroupsIcon />}
                    loading={isSubmitting || photosUploading}
                    onClick={handleSubmit}
                    style={{ marginTop: 10, marginLeft: 12 }}
                    color="primary"
                    variant="contained"
                  >
                    Create Group
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Dialog>
    </>
  );
}
